import React, { useState } from 'react';
import { corelCommunication } from '../lib/corel/communication';
import { FolderOpenIcon as FolderOpen, SaveIcon as Save, XIcon as X } from './icons';
import { useUnifiedPermissions } from '../hooks/useUnifiedPermissions';
import { PermissionDenied } from './ui';
import { clearAllToolsCache } from '../lib/apiCache';
import { sendToTerminal } from '../lib/terminalLogger';
import { useDisplayUnit } from '../hooks/useDisplayUnit';
import { DISPLAY_UNITS } from '../lib/units';

interface SettingsProps {
  onClose: () => void;
}

export function Settings({ onClose }: SettingsProps) {
  const [directory, setDirectory] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);
  const [cacheClearedCount, setCacheClearedCount] = useState(0);
  const { displayUnit, setDisplayUnit } = useDisplayUnit();

  // 🛡️ SEGURANÇA: Validação via permissionConfig (admin sempre tem acesso)
  const { checkFeature } = useUnifiedPermissions();
  const permission = checkFeature('general.can_access_settings');

  if (!permission.allowed) {
    return (
      <PermissionDenied
        status={permission.status}
        title="Configurações"
        onBack={onClose}
      />
    );
  }

  const handleSelectDirectory = () => {
    try { corelCommunication.sendActionGuarded('selecionarDiretorio', {}); } catch {}
  };

  const handleSave = async () => {
    if (!directory) return;

    setIsSaving(true);

    try { corelCommunication.sendActionGuarded('salvarConfiguracoes', { diretorio: directory } as any); } catch {}

    // Simulate saving delay
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsSaving(false);
    onClose();
  };

  const handleClearCache = () => {
    // Limpar cache de ambas as áreas
    const sessionCount = clearAllToolsCache('session');
    const localCount = clearAllToolsCache('local');
    const totalCount = sessionCount + localCount;

    setCacheClearedCount(totalCount);
    setCacheCleared(true);

    sendToTerminal('🗑️ [SETTINGS] Cache de ferramentas limpo', {
      sessionKeys: sessionCount,
      localKeys: localCount,
      total: totalCount
    });

    // Reset após 3 segundos
    setTimeout(() => {
      setCacheCleared(false);
    }, 3000);
  };

  return (
    <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-navy-800 rounded-lg w-full max-w-xs animate-fadeIn">
        <div className="flex justify-between items-center px-3 py-2 border-b border-navy-700">
          <h2 className="text-sm font-semibold text-white">Configurações</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors rounded p-1 hover:bg-navy-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-3 py-2 space-y-3">
          {/* Diretório de Arquivos */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Diretório de Arquivos
            </label>
            <div className="flex">
              <input
                type="text"
                value={directory}
                onChange={(e) => setDirectory(e.target.value)}
                placeholder="Selecione o diretório..."
                className="flex-1 px-2 py-1.5 text-xs bg-navy-900 rounded-l text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-400/50"
                readOnly
              />
              <button
                onClick={handleSelectDirectory}
                className="px-2 py-1.5 bg-navy-900 text-gray-400 hover:text-white rounded-r border-l border-navy-700 hover:bg-navy-700 transition-colors"
              >
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Unidade de Medida */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Unidade de Medida
            </label>
            <div className="flex gap-1">
              {DISPLAY_UNITS.map((u) => (
                <button
                  key={u.key}
                  onClick={() => setDisplayUnit(u.key)}
                  className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    displayUnit === u.key
                      ? 'bg-purple-500 text-white'
                      : 'bg-navy-900 text-gray-400 hover:text-white hover:bg-navy-700'
                  }`}
                >
                  {u.suffix}
                </button>
              ))}
            </div>
          </div>

          {/* Cache de Ferramentas */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Cache de Ferramentas
            </label>
            <div className="flex items-center justify-between bg-navy-900 rounded p-2">
              <p className="text-xs text-gray-300">Limpar cache local</p>
              <button
                onClick={handleClearCache}
                disabled={cacheCleared}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  cacheCleared
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-red-600 hover:bg-red-500 text-white'
                }`}
              >
                {cacheCleared ? `Limpo (${cacheClearedCount})` : 'Limpar'}
              </button>
            </div>
          </div>
        </div>

        {directory && (
          <div className="px-3 py-2 border-t border-navy-700">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full flex items-center justify-center px-3 py-1.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {isSaving ? 'Salvando...' : 'Salvar Diretório'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
